import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type {
  ScrollAnchor,
  UseAnchoredScrollParams,
} from '../../types/scroll/scroll-anchor.types';
import {
  captureScrollAnchors,
  restoreScrollAnchor,
} from '../../utils/scroll/scroll-anchor.util';
import { useScrollIntent } from './use-scroll-intent';
import type { ScrollDirection } from '../../types/scroll/scroll-intent.types';
import { getScrollLayoutRevision } from '../../utils/scroll/scroll-layout.util';

/**
 * Owns viewport positioning across content changes, independent of pagination/data.
 * @param params - Opaque content lifetime, anchor adapter and positioning policy.
 * @returns Container binding, end state and explicit position-retention/end actions.
 */
export const useAnchoredScroll = ({
  scopeKey,
  contentKey,
  layoutRevision,
  layoutBoundaryRef,
  hasContent,
  isUpdating = false,
  anchorStrategy,
  initialPosition = 'start',
  followEnd = false,
  endThreshold = 50,
}: UseAnchoredScrollParams) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const containerRef = useMemo(() => ({ current: container }), [container]);
  const [isAtEnd, setIsAtEnd] = useState(initialPosition === 'end');
  const followsEnd = useRef(initialPosition === 'end');
  const initial = useRef(true);
  const anchors = useRef<ScrollAnchor[]>([]);
  const viewportTop = useRef<number | null>(null);
  const observedLayout = useRef<string | null>(null);
  const restorePending = useRef(false);
  const capturedScroll = useRef<Event | null>(null);
  const positionRevision = useMemo(
    () => [contentKey, layoutRevision, isUpdating],
    [contentKey, layoutRevision, isUpdating]
  );
  const recordPosition = useCallback(() => {
    if (!container) return;
    restorePending.current = false;
    anchors.current = captureScrollAnchors(container, anchorStrategy);
    viewportTop.current = container.getBoundingClientRect().top;
  }, [container, anchorStrategy]);
  const readLayout = useCallback(
    () =>
      container
        ? getScrollLayoutRevision(container, layoutBoundaryRef?.current)
        : null,
    [container, layoutBoundaryRef]
  );
  const layoutChanged = useCallback(
    () =>
      observedLayout.current !== null &&
      observedLayout.current !== readLayout(),
    [readLayout]
  );
  const restorePosition = useCallback(
    (preserveUnreachable: boolean) => {
      if (!container) return;
      const requestedTop = restoreScrollAnchor(
        container,
        anchors.current,
        anchorStrategy
      );
      restorePending.current =
        preserveUnreachable &&
        requestedTop !== undefined &&
        requestedTop >= 0 &&
        Math.abs(container.scrollTop - requestedTop) > 1;
      // An expanded viewport may not have room for the old offset yet. Retain
      // the original anchor until it becomes reachable or fresh input replaces it.
      if (!restorePending.current) recordPosition();
      viewportTop.current = container.getBoundingClientRect().top;
    },
    [container, anchorStrategy, recordPosition]
  );
  const recordIntent = useCallback(
    (_direction: ScrollDirection, event: Event) => {
      if (!container) return false;
      const atEnd =
        container.scrollHeight - container.scrollTop - container.clientHeight <=
        endThreshold;
      followsEnd.current = atEnd;
      setIsAtEnd(atEnd);
      // The capture-phase listener already saved ordinary scrolling. A fresh
      // gesture still replaces anchors when resize/clamp handling skipped capture,
      // or when input at an edge does not produce a scroll event at all.
      if (capturedScroll.current !== event) recordPosition();
      // Keep observing the same wheel/touch/scrollbar gesture through its last frame.
      return false;
    },
    [container, endThreshold, recordPosition]
  );

  useLayoutEffect(() => {
    initial.current = true;
    capturedScroll.current = null;
    restorePending.current = false;
    followsEnd.current = initialPosition === 'end';
    anchors.current = [];
    viewportTop.current = null;
    setIsAtEnd(initialPosition === 'end');
  }, [scopeKey, container, initialPosition]);

  useLayoutEffect(() => {
    if (!container || !hasContent) return;
    if (initial.current) {
      container.scrollTop =
        initialPosition === 'end' ? container.scrollHeight : 0;
      initial.current = false;
    } else if (followEnd && followsEnd.current) {
      container.scrollTop = container.scrollHeight;
    } else {
      // An optional surrounding-layout revision preserves the anchor's screen
      // position when a notice moves the viewport, not only its relative offset.
      if (layoutRevision !== undefined && viewportTop.current !== null) {
        const shift =
          container.getBoundingClientRect().top - viewportTop.current;
        anchors.current = anchors.current.map((anchor) => ({
          ...anchor,
          offset: anchor.offset - shift,
        }));
      }
      restorePosition(restorePending.current || layoutChanged());
      return;
    }
    // Rendering preserves the user's follow/retain choice. Only an actual scroll
    // should reclassify a retained position as following the end.
    recordPosition();
  }, [
    container,
    contentKey,
    layoutRevision,
    hasContent,
    isUpdating,
    scopeKey,
    initialPosition,
    followEnd,
    recordPosition,
    restorePosition,
    layoutChanged,
  ]);

  useLayoutEffect(() => {
    if (!container) return;
    const observesResizing = followEnd && typeof ResizeObserver !== 'undefined';
    const boundary = layoutBoundaryRef?.current;
    observedLayout.current = observesResizing ? readLayout() : null;
    const recordScroll = (event: Event) => {
      if (event.target !== container) return;
      capturedScroll.current = null;
      // Browser clamping can dispatch scroll before ResizeObserver. Keep the
      // pre-resize anchor until restoration; actual gestures record it separately.
      if (!restorePending.current && (!observesResizing || !layoutChanged())) {
        recordPosition();
        capturedScroll.current = event;
      }
    };
    // Run before the intent listener even if this effect restarts independently.
    container.addEventListener('scroll', recordScroll, true);
    const observer = observesResizing
      ? new ResizeObserver(() => {
          if (!layoutChanged()) return;
          observedLayout.current = readLayout();
          // The surrounding layout can clamp scrollTop even when the dock restores
          // the list's original dimensions before this callback. Preserve both
          // the follow choice and the reader's previous anchor.
          if (followsEnd.current) {
            container.scrollTop = container.scrollHeight;
            recordPosition();
          } else {
            restorePosition(true);
          }
        })
      : undefined;
    observer?.observe(container);
    if (boundary && boundary !== container) observer?.observe(boundary);
    return () => {
      container.removeEventListener('scroll', recordScroll, true);
      observer?.disconnect();
      observedLayout.current = null;
    };
  }, [
    container,
    followEnd,
    recordPosition,
    layoutBoundaryRef,
    layoutChanged,
    readLayout,
    restorePosition,
  ]);

  useScrollIntent({
    containerRef,
    layoutBoundaryRef,
    enabled: true,
    resetKey: scopeKey,
    positionRevision,
    onIntent: recordIntent,
  });

  const retainPosition = useCallback(() => {
    if (!container) return;
    initial.current = false;
    followsEnd.current = false;
    setIsAtEnd(false);
    recordPosition();
  }, [container, recordPosition]);

  const scrollToEnd = useCallback(() => {
    if (!container) return;
    initial.current = false;
    followsEnd.current = true;
    restorePending.current = false;
    setIsAtEnd(true);
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [container]);

  return { containerRef, setContainer, isAtEnd, scrollToEnd, retainPosition };
};
